import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import t from './tokens';
import TemplatePreview from './TemplatePreview';

// Full-page package selection (replaces the slide-in shop panel), styled to the
// Shoob v2 Figma "Choose Your Photo Package" screen: a free-shipping banner (only
// when the student's live offer gives free shipping -- see freeShippingBanner), a
// few featured packages as large alternating cards, a grid of standard options,
// trust signals, and a sticky "Continue" bar. Variants are the buyable unit
// (Shop::ProductVariant); the checklist is derived from each variant's print
// slots, and the card image is the customer's own selected photo.
const STRONG = ['Best Value', 'Most Popular'];

const money = (n) => `$${Number(n || 0).toFixed(2)}`;
const dollars = (n) => (Number(n) % 1 === 0 ? `$${Number(n)}` : money(n));

// Free shipping is a COUPON, not a store-wide rule: each product carries
// free_shipping_minimum from the live offer the cart will auto-attach
// (StoreController#with_offer_shipping). null = no free shipping, 0 = every
// order. This used to be a hard-coded "$39" the cart never honoured.
export const freeShippingBanner = (products) => {
  const minimums = products
    .map((p) => p.free_shipping_minimum)
    .filter((m) => m !== null && m !== undefined);
  if (!minimums.length) return null;
  const lowest = Math.min(...minimums);
  return lowest > 0 ? `🎁 FREE shipping on orders over ${dollars(lowest)}` : '🎁 FREE shipping on your order';
};

const includesFreeShipping = (variant) => variant.freeShippingMinimum !== null
  && variant.freeShippingMinimum !== undefined
  && (variant.price || 0) >= variant.freeShippingMinimum;

// CTA label for the sticky bar: "Adding…" while a one-click add is in flight,
// else "Customize →" for multi-sheet packages or "Add to cart" for one-click.
const selectedCtaLabel = (variant, adding) => {
  if (adding) return 'Adding…';
  return variant.multi ? 'Customize →' : 'Add to cart';
};

// Aggregate the variant's print slots into a deduped checklist. A package may
// repeat the same image type (e.g. "All Digital" = 52× "(1) High-Resolution
// Download"); collapse those into one "52 × …" line (and keep keys unique).
const checklist = (variant) => {
  const counts = new Map();
  (variant.slots || []).forEach((sl) => {
    if (!sl.name) return;
    counts.set(sl.name, (counts.get(sl.name) || 0) + 1);
  });
  return [...counts.entries()].map(([label, count]) => ({ label, count }));
};

// Render one checklist line. Image-type names carry their own quantity as a
// leading "(n)" (e.g. "(4) 8x10"); when a type repeats `count` times (e.g. 52
// single downloads) fold it into one "(total) name" line instead of the awkward
// "52 × (1) High-Resolution Download".
const formatLine = ({ label, count }) => {
  const m = label.match(/^\((\d+)\)\s*(.*)$/);
  if (m) return `(${Number(m[1]) * count}) ${m[2]}`;
  return count > 1 ? `${count} × ${label}` : label;
};

const s = {
  page: { background: t.pageBg, minHeight: '100vh', paddingBottom: '6rem' },
  banner: {
    position: 'sticky',
    top: 0,
    zIndex: 30,
    background: t.brand,
    color: '#fff',
    textAlign: 'center',
    padding: '0.7rem 1rem',
    fontWeight: 600,
    fontSize: '0.95rem',
  },
  head: { maxWidth: 960, margin: '0 auto', padding: '1.5rem 1.25rem 0.5rem' },
  back: {
    background: 'none',
    border: 'none',
    color: t.brand,
    cursor: 'pointer',
    padding: 0,
    fontWeight: 600,
    fontSize: '0.9rem',
    marginBottom: '0.75rem',
  },
  h1: {
    fontSize: 'clamp(1.6rem,3vw,2.25rem)', fontWeight: 700, color: t.ink, margin: '0 0 0.25rem', textAlign: 'center',
  },
  sub: {
    color: t.muted, textAlign: 'center', margin: 0, fontSize: '0.95rem',
  },
  body: { maxWidth: 960, margin: '0 auto', padding: '1.5rem 1.25rem' },
  featuredList: {
    display: 'flex', flexDirection: 'column', gap: '1.5rem', marginBottom: '2.5rem',
  },
  fCard: {
    background: t.surface,
    borderRadius: 16,
    overflow: 'hidden',
    boxShadow: t.shadowMd,
    border: '1px solid rgba(0,0,0,.06)',
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit,minmax(260px,1fr))',
  },
  fCardSel: { outline: `3px solid ${t.brand}`, outlineOffset: -1 },
  fImgWrap: {
    position: 'relative',
    minHeight: 260,
    background: '#0b1b2b',
    backgroundSize: 'cover',
    backgroundPosition: 'center',
  },
  // Hero wrapper when a display template is active: no background-image longhands
  // (mixing them with the `background` shorthand triggers React re-render warnings).
  fTplWrap: {
    position: 'relative',
    minHeight: 260,
    background: t.pageBg,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '0.75rem',
  },
  fImg: {
    width: '100%', height: '100%', objectFit: 'cover', display: 'block', minHeight: 260,
  },
  badge: {
    position: 'absolute',
    top: 0,
    right: 0,
    background: 'rgba(0,0,0,.75)',
    color: '#fff',
    padding: '0.4rem 1rem',
    fontSize: '0.8rem',
    fontWeight: 700,
  },
  fContent: {
    padding: '1.5rem', display: 'flex', flexDirection: 'column', justifyContent: 'center',
  },
  fName: {
    fontSize: '1.5rem', fontWeight: 700, color: t.body, margin: '0 0 0.25rem',
  },
  fTagline: { color: t.muted, margin: '0 0 0.75rem' },
  price: { fontSize: '2rem', fontWeight: 700, color: t.brand },
  freeShip: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '0.4rem',
    marginTop: '0.5rem',
    background: t.mutedSurface,
    color: t.brandHover,
    borderRadius: t.pill,
    padding: '0.3rem 0.8rem',
    fontSize: '0.8rem',
    fontWeight: 600,
  },
  ul: {
    listStyle: 'none', padding: 0, margin: '1rem 0', display: 'flex', flexDirection: 'column', gap: '0.5rem',
  },
  li: {
    display: 'flex', alignItems: 'flex-start', gap: '0.5rem', fontSize: '0.92rem', color: t.body,
  },
  check: {
    flex: '0 0 auto',
    width: 18,
    height: 18,
    borderRadius: '50%',
    background: t.brandTint,
    color: t.brand,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '0.7rem',
    fontWeight: 800,
    marginTop: 1,
  },
  selectBtn: {
    border: 'none',
    borderRadius: 12,
    padding: '0.85rem 1.5rem',
    fontWeight: 700,
    cursor: 'pointer',
    fontSize: '1rem',
    background: t.brand,
    color: '#fff',
    marginTop: '0.5rem',
  },
  selectBtnSel: { background: t.success },
  divider: { borderTop: `1px solid ${t.borderStrong}`, paddingTop: '1.5rem' },
  h2: {
    fontSize: '1.25rem', fontWeight: 700, color: t.body, textAlign: 'center', margin: '0 0 1.25rem',
  },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(220px,1fr))', gap: '1rem' },
  sCard: {
    background: t.surface,
    borderRadius: t.radius,
    boxShadow: t.shadowSm,
    border: '1px solid rgba(0,0,0,.06)',
    padding: '1.1rem',
    display: 'flex',
    flexDirection: 'column',
  },
  sCardSel: { outline: `3px solid ${t.brand}` },
  sName: {
    fontSize: '1.05rem', fontWeight: 700, color: t.body, margin: '0 0 0.4rem',
  },
  sPrice: { fontSize: '1.5rem', fontWeight: 700, color: t.ink },
  sBtn: {
    marginTop: 'auto',
    width: '100%',
    padding: '0.65rem',
    borderRadius: t.radius,
    cursor: 'pointer',
    fontWeight: 700,
    fontSize: '0.9rem',
    background: t.surface,
    color: t.ink,
    border: `2px solid ${t.ink}`,
  },
  sBtnSel: { background: t.brand, color: '#fff', border: `2px solid ${t.brand}` },
  stickyBar: {
    position: 'fixed',
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 25,
    background: t.surface,
    borderTop: `1px solid ${t.border}`,
    boxShadow: '0 -4px 16px rgba(0,0,0,.1)',
  },
  stickyInner: {
    maxWidth: 960,
    margin: '0 auto',
    padding: '0.85rem 1.25rem',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '1rem',
  },
  continueBtn: {
    background: t.brand,
    color: '#fff',
    border: 'none',
    borderRadius: t.radius,
    padding: '0.75rem 1.75rem',
    fontWeight: 700,
    cursor: 'pointer',
    whiteSpace: 'nowrap',
    fontSize: '1rem',
  },
  sFreeShip: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '0.35rem',
    marginTop: '0.4rem',
    background: t.mutedSurface,
    color: t.body,
    borderRadius: t.pill,
    padding: '0.2rem 0.6rem',
    fontSize: '0.72rem',
    fontWeight: 600,
  },
  // Trust signals row (Figma: Professional Quality / Fast Delivery / Satisfaction).
  trustWrap: {
    marginTop: '3rem',
    background: t.surface,
    borderRadius: 18,
    boxShadow: t.shadowMd,
    border: '1px solid rgba(0,0,0,.06)',
    padding: '2rem 1.5rem',
  },
  trustGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit,minmax(220px,1fr))',
    gap: '2rem',
    maxWidth: 820,
    margin: '0 auto',
  },
  trustCard: { textAlign: 'center' },
  trustIcon: {
    width: 64,
    height: 64,
    borderRadius: 16,
    background: t.brandTint,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '1.8rem',
    marginBottom: '0.75rem',
  },
  trustTitle: {
    fontSize: '1.05rem', fontWeight: 700, color: t.body, margin: '0 0 0.35rem',
  },
  trustText: {
    fontSize: '0.9rem', color: t.muted, margin: 0, lineHeight: 1.5,
  },
};

// Template (display style) picker shown above the packages.
s.tplBar = {
  display: 'flex',
  alignItems: 'center',
  flexWrap: 'wrap',
  gap: '0.5rem',
  margin: '0 0 1.5rem',
};
s.tplLabel = {
  fontWeight: 600, color: t.body, fontSize: '0.9rem', marginRight: '0.25rem',
};
s.tplChip = {
  flex: '0 0 auto',
  border: `1px solid ${t.brandBorder}`,
  background: t.surface,
  color: t.body,
  borderRadius: t.pill,
  padding: '0.35rem 0.9rem',
  cursor: 'pointer',
  fontSize: '0.85rem',
  fontWeight: 600,
};
s.tplChipActive = {
  border: `1px solid ${t.brand}`, background: t.brand, color: '#fff',
};

const TRUST = [
  { icon: '🖼️', title: 'Professional Quality', text: 'Premium prints on high-quality photo paper.' },
  { icon: '🚚', title: 'Fast Delivery', text: 'Ships within 5-7 business days.' },
  { icon: '💙', title: '100% Satisfaction', text: "Love your photos or we'll make it right." },
];

const PackageSelection = ({
  pose, products, backgroundUrl, templates, studentName, adding, onBack, onSelect,
}) => {
  const [selectedId, setSelectedId] = useState(null);

  const variants = useMemo(
    () => products.flatMap((p) => p.variants.map(
      // Only what this component DRAWS. It used to copy the product's backgrounds on
      // too, purely so they would ride into the builder through onSelect -- a second
      // copy of a fact the builder now looks up itself, and one that every other way
      // of reaching the builder went without.
      (v) => ({
        ...v,
        promoTemplateId: v.promo_template_id, // resolved per-variant hero (own override -> product default)
        freeShippingMinimum: p.free_shipping_minimum,
      }),
    )),
    [products],
  );
  // The product's promo template object (full geometry) for the card hero, if any.
  const promoFor = (variant) => templates.find((tpl) => tpl.id === variant.promoTemplateId) || null;
  const { featured, standard } = useMemo(() => {
    const strong = variants.filter((v) => STRONG.includes(v.badge));
    const feat = (strong.length ? strong : [...variants].sort((a, b) => (b.price || 0) - (a.price || 0)).slice(0, 3));
    const ids = new Set(feat.map((v) => v.id));
    return { featured: feat, standard: variants.filter((v) => !ids.has(v.id)) };
  }, [variants]);

  const selected = variants.find((v) => v.id === selectedId);
  const choose = (variant) => setSelectedId(variant.id); // highlight; the sticky bar confirms
  const heroStyle = backgroundUrl ? { ...s.fImgWrap, backgroundImage: `url(${backgroundUrl})` } : s.fImgWrap;
  const banner = freeShippingBanner(products);

  const renderCheck = (variant) => (
    <ul style={s.ul}>
      {checklist(variant).map(({ label, count }) => (
        <li key={label} style={s.li}>
          <span style={s.check} aria-hidden="true">✓</span>
          <span>{formatLine({ label, count })}</span>
        </li>
      ))}
    </ul>
  );

  return (
    <div style={s.page}>
      {banner && <div style={s.banner}>{banner}</div>}

      <div style={s.head}>
        <button type="button" style={s.back} onClick={onBack}>← Back to photos</button>
        <h1 style={s.h1}>Choose Your Photo Package</h1>
        <p style={s.sub}>Quality photos with flexible options for every family.</p>
      </div>

      <div style={s.body}>
        <div style={s.featuredList}>
          {featured.map((variant, i) => {
            const promo = promoFor(variant);
            return (
              <div key={variant.id} style={selectedId === variant.id ? { ...s.fCard, ...s.fCardSel } : s.fCard}>
                <div style={promo
                  ? { ...s.fTplWrap, order: i % 2 === 1 ? 2 : 0 }
                  : { ...heroStyle, order: i % 2 === 1 ? 2 : 0 }}
                >
                  {promo
                    ? (
                      <TemplatePreview
                        template={promo}
                        photoUrl={pose.watermark_url}
                        backgroundUrl={backgroundUrl}
                        studentName={studentName}
                      />
                    )
                    : (pose.watermark_url && <img src={pose.watermark_url} alt="" style={s.fImg} />)}
                  {STRONG.includes(variant.badge) && <span style={s.badge}>{variant.badge}</span>}
                </div>
                <div style={s.fContent}>
                  <h3 style={s.fName}>{variant.name}</h3>
                  {variant.digital && <p style={s.fTagline}>Instant high-resolution digital downloads</p>}
                  <div>
                    <span style={s.price}>{money(variant.price)}</span>
                    {includesFreeShipping(variant) && <span style={s.freeShip}>🎁 Includes FREE shipping</span>}
                  </div>
                  {renderCheck(variant)}
                  <button
                    type="button"
                    style={selectedId === variant.id ? { ...s.selectBtn, ...s.selectBtnSel } : s.selectBtn}
                    disabled={adding}
                    onClick={() => choose(variant)}
                  >
                    {selectedId === variant.id ? 'Selected ✓' : `Select ${variant.name}`}
                  </button>
                </div>
              </div>
            );
          })}
        </div>

        {standard.length > 0 && (
          <div style={s.divider}>
            <h2 style={s.h2}>Additional Options</h2>
            <div style={s.grid}>
              {standard.map((variant) => (
                <div key={variant.id} style={selectedId === variant.id ? { ...s.sCard, ...s.sCardSel } : s.sCard}>
                  <h3 style={s.sName}>
                    {variant.name}
                    {variant.digital && <span style={{ ...s.check, display: 'none' }} />}
                  </h3>
                  <div style={{ marginBottom: '0.5rem' }}>
                    <span style={s.sPrice}>{money(variant.price)}</span>
                    {includesFreeShipping(variant) && (
                      <div><span style={s.sFreeShip}>🎁 Includes FREE shipping</span></div>
                    )}
                  </div>
                  {renderCheck(variant)}
                  <button
                    type="button"
                    style={selectedId === variant.id ? { ...s.sBtn, ...s.sBtnSel } : s.sBtn}
                    disabled={adding}
                    onClick={() => choose(variant)}
                  >
                    {selectedId === variant.id ? 'Selected ✓' : 'Select package'}
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {variants.length === 0 && (
          <p style={{ color: t.muted, textAlign: 'center' }}>
            No packages are available for your school yet.
          </p>
        )}

        {variants.length > 0 && (
          <div style={s.trustWrap}>
            <div style={s.trustGrid}>
              {TRUST.map((item) => (
                <div key={item.title} style={s.trustCard}>
                  <span style={s.trustIcon} aria-hidden="true">{item.icon}</span>
                  <h4 style={s.trustTitle}>{item.title}</h4>
                  <p style={s.trustText}>{item.text}</p>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {selected && (
        <div style={s.stickyBar}>
          <div style={s.stickyInner}>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: '0.75rem', color: t.muted }}>Selected package</div>
              <div style={{ fontWeight: 700, color: t.ink }}>{selected.name}</div>
              <div style={{ color: t.brand, fontWeight: 600 }}>{money(selected.price)}</div>
            </div>
            {/* multi packages go to the builder ("Customize"), so `adding` only fires on one-click add */}
            <button
              type="button"
              style={adding ? { ...s.continueBtn, opacity: 0.6, cursor: 'not-allowed' } : s.continueBtn}
              disabled={adding}
              onClick={() => onSelect(selected)}
            >
              {selectedCtaLabel(selected, adding)}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

PackageSelection.propTypes = {
  pose: PropTypes.shape({ watermark_url: PropTypes.string }).isRequired,
  products: PropTypes.arrayOf(PropTypes.object).isRequired,
  backgroundUrl: PropTypes.string,
  templates: PropTypes.arrayOf(PropTypes.object),
  studentName: PropTypes.string,
  adding: PropTypes.bool,
  onBack: PropTypes.func.isRequired,
  onSelect: PropTypes.func.isRequired,
};

PackageSelection.defaultProps = {
  backgroundUrl: null,
  templates: [],
  studentName: null,
  adding: false,
};

export default PackageSelection;
